import { UseQueryOptions } from '@tanstack/react-query';
import { PaginationState } from '@tanstack/react-table';
import { useDataConfig } from '../utils/hooks';
import { useMutation, useQuery } from '../utils/useQuery';
import { QueryKeys } from '../constants';
import {
  IdDumpDto,
  ScheduledValidatorIdDumpDto,
  ValidatorControllerService,
  ValidatorDto,
  ValidatorEditDto,
  ValidatorIdDumpsControllerService,
  ValidatorIdDumpScheduleDto,
  ValidatorResponseDto,
  ValidatorFilterResponseDto,
  ValidatorPrefillControllerService,
  NetworkInfoDto,
  CheckQuorumRequest,
  type ScheduledValidatorBackupDto,
  ValidatorBackupControllerService,
  type ValidatorBackupDto,
  ValidatorBackupPropertiesDto,
  ScheduledValidatorPruneDto,
  ValidatorPruneControllerService,
  ScheduledValidatorPruneExtendedDto,
  type ValidatorRestoreResponseDto,
  ValidatorRestoreControllerService,
  type ValidatorRestoreDto,
  type WalletUserUpdateDto,
} from '../openapi';

export const useValidators = (
  options?: UseQueryOptions<ValidatorFilterResponseDto, any, ValidatorFilterResponseDto, any>,
  pagination?: PaginationState,
  status?: ValidatorDto.phase,
  name?: string,
) => {
  let from: number | undefined;
  let limit: number | undefined;
  if (pagination) {
    const { pageSize, pageIndex } = pagination as PaginationState;
    from = pageIndex;
    limit = pageSize;
  }
  return useQuery(
    [QueryKeys.VALIDATORS, from ?? null, limit ?? null, status, name],
    () => ValidatorControllerService.listValidators(from && limit ? from * limit : undefined, status, limit, name),
    {
      select: ({ validators, totalCount }) => {
        const list = validators?.map((el) => {
          return {
            value: el.name,
            label: el.name,
            ...el,
          };
        });

        return { validators: list, totalCount };
      },
      refetchInterval: 7000,
      keepPreviousData: true,
      ...options,
    },
  );
};

export const useValidator = (
  name: string,
  options?: UseQueryOptions<ValidatorResponseDto, any, ValidatorResponseDto>,
) => {
  const { pollingInterval } = useDataConfig();
  return useQuery([QueryKeys.VALIDATORS, name], () => ValidatorControllerService.getValidator(name), {
    refetchInterval: pollingInterval,
    ...options,
  });
};

export const useCreateValidator = () => {
  return useMutation(async (values: ValidatorDto) => {
    const response = await ValidatorControllerService.createValidator(values);
    return response;
  });
};

export const useDeleteValidator = (deleteResources: boolean) => {
  return useMutation(async (name: string) => {
    await ValidatorControllerService.deleteValidator(name, deleteResources);
  });
};

export const useUpdateValidator = () => {
  return useMutation(async (values: ValidatorEditDto) => {
    await ValidatorControllerService.updateValidator(values);
  });
};

export const useChangeWalletUser = (name: string) => {
  return useMutation(async (values: WalletUserUpdateDto) => {
    const response = await ValidatorControllerService.changeWalletUser(name, values);
    return response;
  });
};

export const useNetworksInfo = (options?: UseQueryOptions<NetworkInfoDto, any, NetworkInfoDto>) => {
  return useQuery([QueryKeys.VALIDATORS, 'networks'], () => ValidatorPrefillControllerService.getNetworkInfo(), {
    ...options,
  });
};

export const useCheckQuorum = () => {
  return useMutation(async (sponsorScanAddress: CheckQuorumRequest) => {
    const response = await ValidatorPrefillControllerService.checkQuorum(sponsorScanAddress);
    return response;
  });
};

/* Backups */
export const useValidatorBackupSchedules = (
  name: string,
  options?: UseQueryOptions<unknown, any, ScheduledValidatorBackupDto>,
) => {
  return useQuery(
    [QueryKeys.VALIDATORS, name, QueryKeys.BACKUP],
    () => ValidatorBackupControllerService.getBackupSchedule(name),
    options,
  );
};

export const useCreateValidatorBackupSchedule = (name: string) => {
  return useMutation(async (values: ValidatorBackupDto) => {
    await ValidatorBackupControllerService.createBackupSchedule(name, values);
  });
};

export const useUpdateValidatorBackupSchedule = (name: string) => {
  return useMutation(async (values: ValidatorBackupDto) => {
    await ValidatorBackupControllerService.updateBackupSchedule(name, values);
  });
};

export const useDeleteValidatorBackupSchedule = () => {
  return useMutation(async (name: string) => {
    await ValidatorBackupControllerService.deleteBackupSchedule(name);
  });
};

export const useValidatorBackupHistory = (
  name: string,
  options?: UseQueryOptions<unknown, any, ValidatorBackupPropertiesDto[]>,
) => {
  return useQuery(
    [QueryKeys.VALIDATORS, name, QueryKeys.BACKUP, QueryKeys.HISTORY],
    async () => {
      const res = await ValidatorBackupControllerService.getBackupHistory(name);
      return res.backupList ?? [];
    },
    { ...options },
  );
};

/* Pruning */
export const useValidatorPruningSchedules = (
  name: string,
  options?: UseQueryOptions<unknown, any, ScheduledValidatorPruneExtendedDto>,
) => {
  return useQuery(
    [QueryKeys.VALIDATORS, name, QueryKeys.PRUNE],
    () => ValidatorPruneControllerService.getValidatorPruningSchedule(name),
    options,
  );
};

export const useCreateValidatorPruningSchedule = (name: string) => {
  return useMutation(async (values: ScheduledValidatorPruneDto) => {
    await ValidatorPruneControllerService.createValidatorPruningSchedule(name, values);
  });
};

export const useUpdateValidatorPruningSchedule = (name: string) => {
  return useMutation(async (values: ScheduledValidatorPruneDto) => {
    await ValidatorPruneControllerService.updateValidatorPruningSchedule(name, values);
  });
};

export const useDeleteValidatorPruningSchedule = () => {
  return useMutation(async (name: string) => {
    await ValidatorPruneControllerService.deleteValidatorPruningSchedule(name);
  });
};

/* Restore */
export const useValidatorRestoreJob = (
  name: string,
  options?: UseQueryOptions<unknown, any, ValidatorRestoreResponseDto>,
) => {
  return useQuery(
    [QueryKeys.VALIDATORS, name, QueryKeys.RESTORE],
    () => ValidatorRestoreControllerService.getRestoreJob(name),
    options,
  );
};

export const useCreateValidatorRestoreJob = (name: string) => {
  return useMutation(async (values: ValidatorRestoreDto) => {
    await ValidatorRestoreControllerService.createRestoreJob(name, values);
  });
};

/* Id-dumps */
export const useCreateIdentitiesDump = (name: string) => {
  return useMutation(async () => {
    const response = await ValidatorIdDumpsControllerService.createIdentityDump(name);
    return response;
  });
};

export const useParticipantIdentityDumps = (options?: UseQueryOptions<IdDumpDto[], any, IdDumpDto[]>) => {
  return useQuery(
    [QueryKeys.VALIDATORS, QueryKeys.DUMPS],
    () => ValidatorIdDumpsControllerService.listAllParticipantIdentityDumps(),
    {
      select: (res) =>
        res.map((el) => ({
          value: el.secretName,
          label: el.secretName,
          ...el,
        })),
      ...options,
    },
  );
};

export const useValidatorIdentityDumpSchedule = (
  name: string,
  options?: UseQueryOptions<unknown, any, ScheduledValidatorIdDumpDto>,
) => {
  return useQuery(
    [QueryKeys.VALIDATORS, name, QueryKeys.DUMPS, 'schedule'],
    () => ValidatorIdDumpsControllerService.getSchedule(name),
    options,
  );
};

export const useCreateValidatorIdentityDumpSchedule = (name: string) => {
  return useMutation(async (values: ValidatorIdDumpScheduleDto) => {
    await ValidatorIdDumpsControllerService.createSchedule(name, values);
  });
};

export const useUpdateValidatorIdentityDumpSchedule = (name: string) => {
  return useMutation(async (values: ValidatorIdDumpScheduleDto) => {
    await ValidatorIdDumpsControllerService.updateSchedule(name, values);
  });
};

export const useDeleteValidatorIdentityDumpSchedule = () => {
  return useMutation(async (name: string) => {
    await ValidatorIdDumpsControllerService.deleteSchedule(name);
  });
};

export const useValidatorIdentityDumps = (name: string, options?: UseQueryOptions<IdDumpDto[], any, IdDumpDto[]>) => {
  return useQuery(
    [QueryKeys.VALIDATORS, name, QueryKeys.DUMPS],
    () => ValidatorIdDumpsControllerService.listParticipantIdentityDumps(name),
    {
      select: (res) =>
        res.map((el) => ({
          value: el.secretName,
          label: el.secretName,
          ...el,
        })),
      ...options,
    },
  );
};

export const useDeleteValidatorIdentityDumps = () => {
  return useMutation(async (name: string) => {
    await ValidatorIdDumpsControllerService.deleteAllIdentityDumps(name);
  });
};

export const useDeleteValidatorIdentityDump = (name: string) => {
  return useMutation(async (secretName: string) => {
    await ValidatorIdDumpsControllerService.deleteIdentityDump(name, secretName);
  });
};
